import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Package, Truck, ArrowRight, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { formatINR, resolveImage } from '@/lib/format';

export default function OrderSuccess() {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => setOrder(null)).finally(() => setLoading(false));
    }, [orderId]);

    if (loading) return <div className="py-32 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[var(--brand-blue)]" /></div>;
    if (!order) return <div className="py-32 text-center"><h1 className="font-display text-2xl font-bold">Order not found</h1><Link to="/shop" className="btn-blue mt-6 inline-flex h-11 px-6 rounded-xl font-semibold items-center">Continue shopping</Link></div>;

    const addr = order.shipping_address || {};

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="text-center">
                <div className="h-20 w-20 rounded-full bg-green-100 grid place-items-center mx-auto"><CheckCircle2 className="h-11 w-11 text-green-600" /></div>
                <h1 className="font-display text-3xl lg:text-4xl font-bold mt-5">Thank you for your order!</h1>
                <p className="text-[var(--ink-muted)] mt-2">A confirmation has been sent to <b>{order.email}</b></p>
                <p className="mt-4 inline-block rounded-full bg-[var(--surface-2)] px-4 py-2 text-sm">Order number: <b data-testid="order-confirmation-number">{order.order_number}</b></p>
            </div>

            <div className="mt-8 rounded-2xl border border-border bg-white overflow-hidden">
                <div className="px-6 py-4 border-b border-border flex items-center gap-2"><Package className="h-5 w-5 text-[var(--brand-blue)]" /><span className="font-display font-semibold">Order details</span><span className="ml-auto text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold uppercase">{order.status}</span></div>
                <div className="p-6 space-y-4">
                    {order.items.map((it) => (
                        <div key={it.product_id} className="flex items-center gap-4">
                            <img src={resolveImage(it.image)} alt={it.name} loading="lazy" decoding="async" className="h-16 w-16 rounded-xl object-contain bg-[var(--surface-2)] p-1.5" />
                            <div className="flex-1"><p className="font-medium">{it.name}</p><p className="text-sm text-[var(--ink-muted)]">Qty {it.qty}</p></div>
                            <span className="font-display font-semibold">{formatINR(it.line_total || it.price * it.qty)}</span>
                        </div>
                    ))}
                </div>
                <div className="px-6 py-4 border-t border-border space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--ink-muted)]">Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--ink-muted)]">Shipping</span><span>{order.shipping === 0 ? 'FREE' : formatINR(order.shipping)}</span></div>
                    <div className="flex justify-between text-base font-display font-bold pt-2 border-t border-border"><span>Total paid</span><span>{formatINR(order.total)}</span></div>
                </div>
            </div>

            <div className="mt-6 rounded-2xl border border-border bg-white p-6">
                <div className="flex items-center gap-2"><Truck className="h-5 w-5 text-[var(--brand-blue)]" /><span className="font-display font-semibold">Shipping to</span></div>
                <p className="mt-3 text-sm text-[var(--ink)]">{addr.name} · {addr.phone}</p>
                <p className="text-sm text-[var(--ink-muted)]">{[addr.line1, addr.line2, addr.landmark, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Link to="/shop" className="btn-blue h-12 px-7 rounded-xl font-semibold inline-flex items-center gap-2">Continue shopping <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/account" className="h-12 px-7 rounded-xl font-semibold border border-border bg-white hover:bg-[var(--surface-2)] inline-flex items-center transition-colors">View my orders</Link>
            </div>
        </div>
    );
}
